import { useState, useEffect } from 'react';
import AuthAnimatedModal from 'components/shared/modal/AuthAnimatedModal';
import SchoolFeesPreviewModal from 'components/shared/modal/SchoolFeesPreviewModal';
import { RoutePaths } from 'routes/route-paths';
import { useNavigate } from 'react-router-dom';
import {
  sendCatchFeedback,
  sendFeedback,
} from '../../../../functions/feedback';
import { appAxios } from '../../../../api/axios';
import Fireworks from 'react-canvas-confetti/dist/presets/fireworks';
import * as yup from 'yup';
import { v4 as uuidv4 } from 'uuid';

import { formatToNaira } from '../../../../functions/currencyConveter';
import ProgresLoader from '../../../../components/shared/loader/successLoader';
import { getTokenDetails } from 'functions/userSession';
import { decodeToken } from 'react-jwt';
import { TokenType } from 'types/jwt';
import MyChildLoanApplStep1 from 'components/user/individualSchoolSteps/myChildLoan/Step1';
import CustomStepper from 'components/shared/stepper';
import MyChildLoanApplStep2 from 'components/user/individualSchoolSteps/myChildLoan/Step2';

interface FormData {
  LoanAmount: string;
  utilityBill: File | null;
  validId: File | null;

  ChildDetails: Array<{
    ChildFirstName: string;
    ChildLastName: string;
    SchoolFees: string;
    SchoolName: string;
    SchoolLocation: string;
    SchoolAddress: string;
    Address2: string;
    City: string;
    PostalCode: string;
    Country: string;
    ChildGrade: string;
    invoice: File | null;
  }>;
  ParentRecords_Occupation: string;
  Address2: string;
  City: string;
  PostalCode: string;
  Country: string;
  State: string;
  ParentRecords_CompanyName: string;
  ParentRecords_Location: string;
  ParentRecords_YearsOfWorkinWithThem: string;
  MonthsOfWorkinWithThem: string;
  ParentRecords_CompanyEmail: string;
  ParentRecords_CompanyPhoneNumber: string;
  ParentRecords_PaySlip: File | null;
  ParentRecords_BankStatement: File | null;
}

const stepValidationSchemas = [
  yup.object({
    FirstName: yup.string().required('First name is required'),
    LastName: yup.string().required('Last name is required'),
    Email: yup
      .string()
      .test('is-valid-email', 'Invalid email address!', (value) => {
        if (!value) return false;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(value);
      })
      .required('Email address required!'),
    PhoneNumber: yup
      .string()
      .matches(/^[0-9]{1,13}$/, 'Phone number must be 13 digits including 234')
      .required('Phone number is required'),
    LoanAmount: yup.string().required('Loan amount is required'),
  }),
  yup.array().of(
    yup.object({
      ChildFirstName: yup.string().required('Child first name is required'),
      ChildLastName: yup.string().required('Child last name is required'),
      SchoolFees: yup.string().required('School fees is required'),
      SchoolName: yup.string().required('School name is required'),
      SchoolLocation: yup.string().required('School location is required'),
      SchoolAddress: yup.string().required('School address is required'),
      // nameOfSchool: Yup.string().required('Required'),
    })
  ),
  yup.object({
    ParentRecords_Occupation: yup.string().required('Occupation is required'),
    ParentRecords_CompanyName: yup
      .string()
      .required('Company name is required'),
    ParentRecords_CompanyPhoneNumber: yup
      .string()
      .required('Company Phone number is required'),
    ParentRecords_Location: yup
      .string()
      .required('Company location is required'),
    ParentRecords_YearsOfWorkinWithThem: yup
      .number()
      .required('Years of work required'),
    ParentRecords_CompanyEmail: yup
      .string()
      .email('Invalid email format')
      .required('Email is required'),
    ParentRecords_PaySlip: yup
      .mixed()
      .nullable()
      .notRequired()
      .test(
        'fileSize',
        'File size is too large. Maximum size is 5MB.',
        (value: any) => {
          return value && value.size <= 5 * 1024 * 1024;
        }
      )
      .test(
        'fileType',
        'Unsupported file format. Only PDF, JPEG, or PNG is allowed.',
        (value) => {
          if (!value) return true; // ✅ allow empty (optional)
          return (
            value instanceof File &&
            ['application/pdf', 'image/jpeg', 'image/png'].includes(value.type)
          );
        }
      ),

    ParentRecords_BankStatement: yup
      .mixed<File>()
      .required('A file is required')
      .test(
        'fileSize',
        'File size is too large. Maximum size is 5MB.',
        (value) => {
          return value && value.size <= 5 * 1024 * 1024;
        }
      )
      .test(
        'fileType',
        'Unsupported file format. Only PDF, JPEG, or PNG is allowed.',
        (value) => {
          return (
            value instanceof File &&
            ['application/pdf', 'image/jpeg', 'image/png'].includes(value.type)
          );
        }
      ),
  }),
];

const MyChildSchoolFeePage = () => {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(0);
  const [idempotencyKey, setIdempotencyKey] = useState(uuidv4());
  const [CustomerId, setCustomerId] = useState('');

  const [loading, setLoading] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const getClientId = () => {
    const token = getTokenDetails();
    if (!token) {
      return;
    }
    const decodedToken = decodeToken<TokenType>(token);
    if (decodedToken) {
      setCustomerId(decodedToken?.customerId);
    }
  };

  useEffect(() => {
    getClientId();
  }, []);
  const handleProceed = () => {
    setIsModalOpen(false);
    navigate(RoutePaths.USER_DASHBOARD);
  };
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const clearErrors = (field: string) => {
    setErrors((prevErrors) => ({ ...prevErrors, [field]: '' }));
  };
  const [inputData, setInputData] = useState<FormData>({
    LoanAmount: '',
    utilityBill: null,
    validId: null,
    ChildDetails: [],
    ParentRecords_Occupation: '',
    Address2: '',
    City: '',
    PostalCode: '',
    Country: '',
    State: '',
    ParentRecords_CompanyName: '',
    ParentRecords_Location: '',
    ParentRecords_CompanyEmail: '',
    ParentRecords_CompanyPhoneNumber: '',
    ParentRecords_YearsOfWorkinWithThem: '',
    MonthsOfWorkinWithThem: '',
    ParentRecords_PaySlip: null,
    ParentRecords_BankStatement: null,
  });

  const validateStep = async (step: number) => {
    try {
      if (step === 1) {
        await stepValidationSchemas[step].validate(inputData.ChildDetails, {
          abortEarly: false,
        });
      } else {
        await stepValidationSchemas[step].validate(inputData, {
          abortEarly: false,
        });
      }
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const formattedErrors: { [key: string]: string } = {};
        error.inner.forEach((err) => {
          if (err.path) {
            formattedErrors[err.path] = err.message;
          }
        });
        setErrors(formattedErrors);
      } else {
        console.error('Unexpected error', error);
      }
      return false;
    }
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep((prevStep) => prevStep + 1);
    }
  };

  const prevStep = () => setCurrentStep((prev) => Math.max(prev - 1, 0));

  const handleFormData = (data: Partial<FormData>) => {
    setInputData((prev) => ({ ...prev, ...data }));
  };

  const loanAmount = parseFloat(
    inputData?.LoanAmount.toString().replace(/,/g, '')
  );

  const getPreviewContent = () => {
    return (
      <div>
        <>
          <h4 className="text-left font-semibold mb-1">
            Parent/Guardian's Details
          </h4>
          <div className="grid grid-cols-1 text-left mb-3 sm:grid-cols-2 gap-4">
            <div className="grid-cols-1">
              <p>
                <strong>Loan Amount:</strong> {formatToNaira(loanAmount) || 0}
              </p>
            </div>
          </div>
        </>

        <>
          <h4 className="text-left font-semibold mb-1">Company's Details</h4>
          <div className="grid grid-cols-1 text-left mb-3 sm:grid-cols-2 gap-4">
            <div>
              <p>
                <strong>Parent Occupation:</strong>{' '}
                {inputData.ParentRecords_Occupation}
              </p>

              <p>
                <strong>Company's Name:</strong>{' '}
                {inputData.ParentRecords_CompanyName}
              </p>
              <p>
                <strong>Company's Phone Number:</strong>{' '}
                {`+${inputData.ParentRecords_CompanyPhoneNumber}`}
              </p>
            </div>
            <div>
              <p>
                <strong>Company's Address:</strong>{' '}
                {/* {inputData.ParentRecords_Location} */}
                {`${inputData.ParentRecords_Location}
                 ${inputData.Address2}
                 ${inputData.City}
                 ${inputData.State}
                 ${inputData.Country}`}
              </p>

              <p>
                <strong>Company's Offical Email Address:</strong>{' '}
                {inputData.ParentRecords_CompanyEmail}
              </p>
              <p>
                <strong>Duration Of Working With the Company:</strong>{' '}
                {`${inputData.ParentRecords_YearsOfWorkinWithThem}years ${inputData.MonthsOfWorkinWithThem}months`}
              </p>
            </div>
          </div>
        </>
        <>
          <h4 className="text-left font-semibold mb-1">Children Details</h4>
          <div className="">
            {inputData.ChildDetails && inputData.ChildDetails.length > 0 ? (
              inputData.ChildDetails.map((child, index) => {
                const schoolFeeAmount = parseFloat(
                  child?.SchoolFees.toString().replace(/,/g, '')
                );
                return (
                  <div
                    key={index}
                    className="grid grid-cols-1 text-left mb-3 sm:grid-cols-2 gap-4 "
                  >
                    <div>
                      <p>
                        <strong>Child's First Name:</strong>{' '}
                        {child?.ChildFirstName}
                      </p>
                      <p>
                        <strong>Child's Last Name:</strong>{' '}
                        {child?.ChildLastName}
                      </p>
                      <p>
                        <strong>Child's Grade:</strong> {child.ChildGrade}
                      </p>
                    </div>
                    <div>
                      <p>
                        <strong>School Name:</strong> {child.SchoolName}
                      </p>
                      <p>
                        <strong>State:</strong> {child.SchoolLocation}
                      </p>
                      <p>
                        <strong>School Address:</strong>
                        {/* {child.SchoolAddress} */}
                        {`
                        ${child.SchoolAddress || child.Address2}
                       ${child.City}
                       ${child.SchoolLocation}
                       ${child?.PostalCode || ''}
                       ${child.Country}`}
                      </p>
                    </div>
                    <div>
                      <p>
                        <strong>School Fee:</strong>{' '}
                        {formatToNaira(schoolFeeAmount) || 0}
                      </p>
                    </div>
                  </div>
                );
              })
            ) : (
              <p>No child details available.</p>
            )}
          </div>
        </>
      </div>
    );
  };

  const handlePreview = () => {
    setIsPreviewModalOpen(true);
  };

  const handlePreviewConfirm = () => {
    setIsPreviewModalOpen(false);
    completeSchoolForm();
  };

  const completeSchoolForm = async () => {
    setLoading(true);
    const formData = new FormData();
    const isValid = await validateStep(currentStep);

    formData.append(`LoanAmount`, loanAmount.toString());

    inputData.ChildDetails.forEach((child, index) => {
      formData.append(
        `ChildDetails[${index}].ChildFirstName`,
        child.ChildFirstName
      );
      formData.append(
        `ChildDetails[${index}].ChildLastName`,
        child.ChildLastName
      );
      formData.append(
        `ChildDetails[${index}].SchoolFee`,
        child.SchoolFees.toString()
      );
      formData.append(`ChildDetails[${index}].SchoolName`, child.SchoolName);
      formData.append('CustomerId', CustomerId);

      formData.append(
        `ChildDetails[${index}].SchoolLocation`,
        child.SchoolLocation
      );
      formData.append(
        `ChildDetails[${index}].SchoolAddress`,
        `${child.SchoolAddress} ${child.Address2} ${child.City} ${child.SchoolLocation} ${child.PostalCode || ''} ${child.Country}`
      );
      formData.append('IdempotencyKey', idempotencyKey);

      formData.append(`ChildDetails[${index}].ChildGrade`, child.ChildGrade);
      // if (child.invoice && child.invoice.length > 0) {
      //   child.invoice.forEach((file, fileIndex) => {
      //     formData.append(`ChildDetails[${fileIndex}].invoice`, file);
      //   });
      // }
      if (child.invoice) {
        formData.append(
          `ChildDetails[${index}].invoice`,
          child.invoice,
          child.invoice.name
        );
      }
      // if (child.invoice) {
      //   formData.append(`ChildDetails[${index}].invoice`, child.invoice);
      // }
    });
    formData.append(
      'ParentRecords.Occupation',
      inputData.ParentRecords_Occupation
    );
    formData.append(
      'ParentRecords.CompanyName',
      inputData.ParentRecords_CompanyName
    );
    formData.append(
      'ParentRecords.CompanyLocation',
      `${inputData.ParentRecords_Location} ${inputData.Address2} ${inputData.City} ${inputData.State} ${inputData.Country}`
    );
    formData.append(
      'ParentRecords.YearsOfWorkinWithThem',
      `${inputData.ParentRecords_YearsOfWorkinWithThem}years ${inputData.MonthsOfWorkinWithThem}months`
    );
    formData.append(
      'ParentRecords.CompanyEmail',
      inputData.ParentRecords_CompanyEmail
    );
    formData.append(
      'ParentRecords.CompanyPhoneNumber',
      inputData.ParentRecords_CompanyPhoneNumber
    );
    if (inputData.utilityBill) {
      formData.append(
        'ParentRecords.UtilityBill',
        inputData.utilityBill,
        inputData?.utilityBill?.name
      );
    } else {
      sendFeedback('Utility bill is required.', 'error');
      setLoading(false);
      return;
    }
    if (inputData.validId) {
      formData.append(
        'ParentRecords.UtilityBill',
        inputData.validId,
        inputData?.validId?.name
      );
    } else {
      sendFeedback('Valid Id is required.', 'error');
      setLoading(false);
      return;
    }

    if (inputData.ParentRecords_PaySlip) {
      formData.append(
        'ParentRecords.PaySlip',
        inputData.ParentRecords_PaySlip,
        inputData.ParentRecords_PaySlip.name
      );
    }

    // Handle BankStatement file (single file)
    if (inputData.ParentRecords_BankStatement) {
      formData.append(
        'ParentRecords.BankStatement',
        inputData.ParentRecords_BankStatement,
        inputData.ParentRecords_BankStatement.name
      );
    } else {
      sendFeedback('Bank statement is required.', 'error');
      setLoading(false);
      return;
    }
    if (isValid) {
      try {
        const response = await appAxios.post(
          '/register-customer-schoolfee-service',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              // 'Idempotency-Key': idempotencyKey,
            },
          }
        );

        if (response.data.success) {
          sendFeedback(response.data?.message, 'success');
          setShowConfetti(true);
          setIsModalOpen(true);
          setLoading(false);
          // generate a new idempotency key for future submissions
          setIdempotencyKey(uuidv4());

          setIsSubmitModalOpen(true);
        } else {
          sendCatchFeedback(response);
          sendFeedback(response.data.message, 'error');
          setLoading(false);
        }
      } catch (error) {
        sendCatchFeedback(error);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showConfetti]);

  useEffect(() => {
    let interval: number | null = null;
    if (loading) {
      const interval = setInterval(() => {
        setProgress((prevProgress) => {
          if (prevProgress >= 100) {
            // clearInterval(interval!);
            // return 100;
            return 1;
          }
          return prevProgress + 5;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [loading]);

  const stepsArray = [
    {
      stepLabel: 'Step:1 Children Information',
      stepDescription: 'Child and School Details',
      completed: currentStep > 0 ? true : false,
    },
    {
      stepLabel: 'Step:2 Job & Income Information',
      stepDescription: 'Parent Employment & Income Details',
      completed: currentStep > 1 ? true : false,
    },
  ];
  return (
    <div className="sm:flex block bg-BrandBg3 py-4 px-6 ">
      {loading && (
        <ProgresLoader progress={progress} titleText="Please wait... " />
      )}
      <div className="w-full flex-1 flex gap-7 sm:flex-row flex-col-reverse ">
        <div className="  bg-white shadow-card p-8 rounded-md">
          {showConfetti && (
            <Fireworks
              autorun={{ speed: 3 }}
              width={windowWidth}
              height={windowHeight}
            />
          )}
          <div className="form-content">
            {currentStep === 0 && (
              <MyChildLoanApplStep1
                inputData={inputData.ChildDetails}
                setInputData={handleFormData}
                errors={errors}
                clearErrors={clearErrors}
                nextStep={nextStep}
              />
            )}
            {currentStep === 1 && (
              <MyChildLoanApplStep2
                inputData={inputData}
                setInputData={handleFormData}
                errors={errors}
                clearErrors={clearErrors}
                prevStep={prevStep}
                nextStep={handlePreview}
                loading={loading}
              />
            )}
          </div>
        </div>
        <CustomStepper currentStep={currentStep} steps={stepsArray} />
      </div>

      <AuthAnimatedModal
        isOpen={isModalOpen}
        onClose={handleProceed}
        onConfirm={handleProceed}
        title="Service Form Submitted!"
        btnText="Proceed"
        content={
          <p>
            You have successfully submitted your service form, Kindly check your
            email to complete the process.
          </p>
        }
      />
      <SchoolFeesPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        title="Preview Your Submission"
        btnText="Submit"
        content={getPreviewContent()}
        onConfirm={handlePreviewConfirm}
      />
    </div>
  );
};

export default MyChildSchoolFeePage;
